import { useCallback, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import Heading from 'LuxKit/Typography/Heading'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import TextAreaOnly from 'components/Common/Form/Input/TextAreaOnly'
import { mediaQueryDown } from 'components/utils/breakpoint'

const STAR_VALUES = [1, 2, 3, 4, 5]
const COMMENT_MAX_LENGTH = 500

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(${rem(18)});
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const Popup = styled.div`
  display: grid;
  gap: ${rem(14)};
  width: 100%;
  margin-bottom: ${rem(14)};
  padding: ${rem(18)};
  border: ${rem(2)} solid ${props => props.theme.palette.product.ai.light};
  border-radius: ${rem(20)};
  background: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: 0 0 ${rem(8)} ${props => props.theme.palette.product.ai.light35},
              0 ${rem(2)} ${rem(12)} ${props => props.theme.palette.product.ai.dark25};
  animation: ${slideUp} 1400ms cubic-bezier(0.05, 0.75, 0.12, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(12)};
`

const StarRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`

const StarButton = styled.button<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(30)};
  height: ${rem(30)};
  padding: 0;
  border: 0;
  background: transparent;
  color: ${props => props.$selected ? props.theme.palette.messaging.warning.normalForeground : props.theme.palette.neutral.default.four};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${props => props.theme.palette.neutral.default.one};
    outline-offset: 2px;
    border-radius: ${props => props.theme.borderRadius.S};
  }
`

const FormRow = styled.form`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${rem(16)};
  align-items: start;

  ${mediaQueryDown.mobile} {
    grid-template-columns: 1fr;
  }
`

const CommentInput = styled(TextAreaOnly)`
  width: 100%;

  textarea {
    min-height: ${rem(48)};
    max-height: ${rem(144)};
    overflow-y: auto;
  }

  && textarea:not(:focus) {
    opacity: 1;
    color: inherit;
    text-shadow: none;
  }
`

interface Props {
  onClose: () => void
  onSubmit: (score: number | undefined, comment: string) => void
}

export default function ChatFeedbackPopup({ onClose, onSubmit }: Props) {
  const intl = useIntl()
  const [score, setScore] = useState<number | undefined>(undefined)
  const [hoveredScore, setHoveredScore] = useState<number | undefined>(undefined)
  const [comment, setComment] = useState('')

  const handleCommentChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(event.currentTarget.value)
  }, [])

  const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (score === undefined) {
      return
    }

    onSubmit(score, comment)
  }, [comment, onSubmit, score])
  const handleClose = useCallback(() => {
    if (score !== undefined || comment.trim() !== '') {
      onSubmit(score, comment)
      return
    }

    onClose()
  }, [comment, onClose, onSubmit, score])

  const heading = intl.formatMessage({
    id: 'RF53oipHXJoQSPH9N6lN',
    defaultMessage: 'How can we improve TripPlanner?',
    description: 'Heading text shown at the top of the ChatTripPlanner feedback popup',
  })
  const closeLabel = intl.formatMessage({
    id: 'JYWe_G1bG_zBemJcVVI2',
    defaultMessage: 'Close feedback popup',
    description: 'Aria-label for closing the ChatTripPlanner feedback popup',
  })
  const commentPlaceholder = intl.formatMessage({
    id: 'GCQ43VdiREzjpnt8h81g',
    defaultMessage: 'Anything we could improve? (optional)',
    description: 'Placeholder for optional ChatTripPlanner feedback comment',
  })
  const submitLabel = intl.formatMessage({
    id: '3KMNXMH5j_VaRo7Hh5-I',
    defaultMessage: 'Submit',
    description: 'Submit button label for ChatTripPlanner feedback popup',
  })

  return (
    <Popup>
      <HeaderRow>
        <Heading variant="heading6">{heading}</Heading>
        <IconButton kind="tertiary" variant="dark" size="small" onClick={handleClose} aria-label={closeLabel}>
          <LineTimesIcon />
        </IconButton>
      </HeaderRow>
      <StarRow
        role="radiogroup"
        aria-label={heading}
        onMouseLeave={() => setHoveredScore(undefined)}
      >
        {STAR_VALUES.map(value => (
          <StarButton
            key={value}
            type="button"
            $selected={value <= (hoveredScore ?? score ?? 0)}
            onClick={() => setScore(value)}
            onMouseEnter={() => setHoveredScore(value)}
            onFocus={() => setHoveredScore(value)}
            onBlur={() => setHoveredScore(undefined)}
            role="radio"
            aria-checked={score === value}
            aria-label={intl.formatMessage(
              {
                id: '8S5fOpz0p9dpWC0wKY3Y',
                defaultMessage: '{value} out of 5 stars',
                description: 'Aria-label for a star rating option in ChatTripPlanner feedback popup',
              },
              { value },
            )}
          >
            <SolidStarIcon size="L" />
          </StarButton>
        ))}
      </StarRow>
      <FormRow onSubmit={handleSubmit}>
        <CommentInput
          value={comment}
          onChange={handleCommentChange}
          placeholder={commentPlaceholder}
          maxLength={COMMENT_MAX_LENGTH}
          resize="none"
          noValidationSpacing
          noValidationMessage
          aria-label={commentPlaceholder}
        />
        <TextButton kind="primary" variant="dark" size="large" type="submit" disabled={score === undefined}>{submitLabel}</TextButton>
      </FormRow>
    </Popup>
  )
}
